import { Icon } from '@blueprintjs/core'
import { Menu, MenuItem } from '../../components/ui/menu'
import React, { type ReactNode, useEffect, useMemo, useState } from 'react'
import { Layout } from '../../components/Layout'
import { useInterval } from '../../hooks/useInterval'
import { Toasts } from '../../components/toaster/Toast'
import { useTableHooksFromStore } from '../../packages/blueprint-components/src/useTableHooks'
import { axiosGet, axiosPatch } from '../../util/api'
import { Popover } from '../../components/ui/popover'
import { Button } from '../../components/ui/button'
import {
    type AgGridColumnDef,
    AgGridTable,
    DummyHeader,
    type DummyHeaderProps,
} from '../../components/table2/AgGridTable'
import { TableStore } from '../../components/table2/table_store'
import { userStore } from '../../stores/user_store'
import { useSavedFiltersStore } from '../../hooks/useSavedFiltersStore'
import type { CellProps } from '../../components/table2/ad_grid_columns'
import { HeaderContextMenu } from '../../components/table2/react_data_grid_columns'
import { observer } from 'mobx-react-lite'

const StaticHeader = observer(
    <T,>({ column: { colDef }, context: { store } }: DummyHeaderProps<T>) => (
        <HeaderContextMenu columnName={colDef.field as string} store={store}>
            <div className="w-full flex-col">
                <div className="flex items-center">{String(colDef.headerName ?? colDef.field)}</div>
            </div>
        </HeaderContextMenu>
    ),
)

const agGridComponent = (component: unknown): ReactNode => component as ReactNode

export const AdminBulkActions = observer(() => {
    const [countdown, setCountdown] = useState<number>(5)
    const [store] = useState<TableStore<StuckBulkAction>>(
        new TableStore(userStore, 'AdminBulkActions'),
    )
    const savedFiltersStore = useSavedFiltersStore()

    const load = (params: any) => {
        return axiosGet('admin/bulk-actions', params)
    }
    const tableHooks = useTableHooksFromStore(load, {}, false, store, undefined, {
        requireProfile: false,
    })

    useInterval(() => {
        console.log(countdown)
        if (countdown == 0) {
            setCountdown(5)
        } else {
            setCountdown(countdown - 1)
        }
    }, 1000) // 1s

    useEffect(() => {
        if (countdown == 0) {
            tableHooks.reload()
        }
    }, [countdown])

    const reloadButton = (
        <Button
            className="ml-2 whitespace-nowrap"
            disabled={tableHooks.loading || countdown == 0}
            text={`Reload Now (${countdown}s)`}
            intent="primary"
            onClick={() => {
                setCountdown(5)
                tableHooks.reload()
            }}
        />
    )

    return (
        <Layout primary="Bulk Actions">
            <AgGridTable
                name={'Bulk Actions'}
                showHeader={false}
                tableHooks={tableHooks}
                store={store}
                savedFiltersStore={savedFiltersStore}
                dateSelect={false}
                columnSelection={false}
                bulkActions={false}
                search
                columns={columns}
                otherActions={reloadButton}
            />
        </Layout>
    )
})

const getCurrentUnixTime = (): number => {
    return Math.round(Date.now() / 1000)
}

const Actions = ({ data }: { data: StuckBulkAction }) => {
    const clearProgress = () => {
        if (!data.uuid) {
            Toasts.error('Cannot clear this marker because its execution ID is missing.')
            return
        }

        axiosPatch(
            'admin/bulk-actions/clear-progress',
            {
                adType: data.ad_type,
                profileId: data.profile_id,
                linkScope: data.link_scope,
                requestUuid: data.uuid,
            },
            false,
            { ignore: true },
        )
            .then((response) => {
                Toasts.success(response.data.message)
            })
            .catch((error) => {
                console.log(error)
                Toasts.error(error.response?.data?.message ?? error.message)
            })
    }

    const menu = useMemo(() => {
        return (
            <Menu>
                <MenuItem
                    disabled={!data.uuid}
                    text={data.uuid ? 'Clear Stale Progress' : 'Cannot clear: execution ID missing'}
                    onClick={clearProgress}
                />
            </Menu>
        )
    }, [data])

    return (
        <Popover content={menu} position="bottom" interactionKind="hover">
            <Button icon={<Icon icon="wrench" />} text="Actions" />
        </Popover>
    )
}

interface StuckBulkAction {
    ad_type: string
    user_id: number
    email: string
    profile_id: string
    link_scope: string
    uuid?: string
    created_at: number
    __actions?: string
}

const RunningForCellRenderer = ({ data }: CellProps<StuckBulkAction>) => {
    const runningFor = getCurrentUnixTime() - data.created_at // seconds

    if (runningFor < 60) {
        return `${runningFor}s`
    }
    return `${(runningFor / 60).toFixed(1)}min`
}

const ActionsCellRenderer = ({ data }: CellProps<StuckBulkAction>) => <Actions data={data} />

const columns: AgGridColumnDef<StuckBulkAction>[] = [
    {
        field: 'ad_type',
        headerName: 'Ad Type',
        headerComponent: agGridComponent(DummyHeader),
    },
    {
        field: 'user_id',
        headerName: 'User Id',
        headerComponent: agGridComponent(StaticHeader),
    },
    {
        field: 'email',
        headerName: 'Email',
        headerComponent: agGridComponent(StaticHeader),
        width: 250,
    },
    {
        field: 'profile_id',
        headerName: 'Profile Id',
        headerComponent: agGridComponent(StaticHeader),
    },
    {
        field: 'created_at',
        headerName: 'Running for',
        headerComponent: agGridComponent(StaticHeader),
        width: 130,
        cellRenderer: agGridComponent(RunningForCellRenderer),
    },
    {
        field: '__actions',
        headerName: 'Actions',
        headerComponent: agGridComponent(DummyHeader),
        cellRenderer: agGridComponent(ActionsCellRenderer),
    },
]
